// src/pages/LoginPage.js

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import keycloak from "../APIs/keycloak";
import { fetchCurrentUser } from "../APIs/authApi";
import { showToast } from "../APIs/utils/toastManager";
import { useBackendReady } from "../APIs/BackendReadyContext";
import { resolveRouteForCurrentUser } from "../APIs/utils/authRouting";

const LoginPage = () => {
  const backendReady = useBackendReady();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [checkingUser, setCheckingUser] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function checkExistingSession() {
      if (!keycloak?.token) {
        setLoading(false);
        return;
      }

      try {
        setCheckingUser(true);
        const currentUser = await fetchCurrentUser();
        if (!isMounted) return;

        if (currentUser && keycloak.authenticated) {
          const route = resolveRouteForCurrentUser(currentUser) || "/";
          navigate(route, { replace: true });
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("Error while checking /me on LoginPage:", err);
        setError("Unable to load your account. Please try again.");
      } finally {
        if (isMounted) {
          setCheckingUser(false);
          setLoading(false);
        }
      }
    }

    checkExistingSession();
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleSignIn = () => {
    if (!backendReady) {
      showToast("MedMaze is loading, please wait.", "info");
      return;
    }

    // Clear stale keycloak callback entries from previous login attempts
    Object.keys(localStorage)
      .filter((k) => k.startsWith("kc-callback-"))
      .forEach((k) => localStorage.removeItem(k));

    const returnTo = location.state?.returnTo || "/login";

    keycloak.login({
      redirectUri:
        (process.env.REACT_APP_SITE_URL || window.location.origin) + returnTo,
    });
  };

  const handleSignUp = () => {
    keycloak.register({
      redirectUri: process.env.REACT_APP_SITE_URL || window.location.origin,
    });
  };

  // While checking if user is already logged in, show a simple loading state
  if (loading || checkingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <p className="text-gray-700">Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-center">
          Welcome to MedMaze
        </h2>

        {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

        {/* Sign In button */}
        <button
          type="button"
          onClick={handleSignIn}
          className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 mb-4"
        >
          Sign In
        </button>

        {/* Divider */}
        <div className="flex items-center my-4">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="mx-2 text-gray-400 text-sm">or</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Sign Up button (was "Create Account") */}
        <div className="text-center">
          <span className="text-gray-600">New to MedMaze?</span>
          <button
            type="button"
            onClick={handleSignUp}
            className="ml-2 text-blue-500 hover:underline font-medium"
          >
            Sign Up
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
